/**
 * Tier-3 custom CSS injection.
 *
 * The buyer enters a CSS snippet in Settings → Branding → Custom CSS.
 * We store it in IndexedDB and, on every dashboard load, inject a
 * <style id="agencylens-custom-css"> tag into <head> so it overrides
 * the default agency-brand styles.
 *
 * Recovery: `?nocss=1` in the URL disables injection just for that
 * session. Documented in failure-mode article #11.
 *
 * Validator: rejects rules that would break the app shell
 *  - `position: fixed` / `position: sticky` outside .agencylens-* scope
 *  - `display: none` on anything that matches `button`, `[role=...]`,
 *    or the AgencyLens root (`#root`, `body`, `html`)
 *  - `@import` (would let custom CSS load arbitrary external sheets)
 *
 * This is best-effort linting, not a sandbox. Browsers run whatever CSS
 * we inject; we're just trying to save the buyer from foot-guns that
 * would lock them out of the UI.
 */

import { idbGet, idbPut, idbDelete } from "./idb.js";

const STORAGE_KEY = "settings:custom-css";
const STYLE_ELEMENT_ID = "agencylens-custom-css";

export interface CustomCSSRecord {
  /** Raw CSS the buyer entered */
  css: string;
  /** False → don't inject even if css is set */
  enabled: boolean;
  updatedAt: string;
}

export interface ValidationResult {
  ok: boolean;
  errors: string[];
  warnings: string[];
}

/* --------------------------------------------------- validator */

const BANNED_PATTERNS: Array<{ pattern: RegExp; message: string }> = [
  {
    pattern: /@import\b/i,
    message: "@import is not allowed — it would load CSS from arbitrary external URLs.",
  },
  {
    pattern: /position\s*:\s*fixed\b/i,
    message: "position: fixed can hide AgencyLens UI underneath. If you need it, scope to .agencylens-customer-page only.",
  },
  {
    pattern: /position\s*:\s*sticky\b/i,
    message: "position: sticky on critical elements can hide the sign-out / settings controls.",
  },
];

const RISKY_DISPLAY_NONE = [
  /(^|[^a-z-])body\s*\{[^}]*display\s*:\s*none/i,
  /(^|[^a-z-])html\s*\{[^}]*display\s*:\s*none/i,
  /#root\s*\{[^}]*display\s*:\s*none/i,
  /button\s*\{[^}]*display\s*:\s*none/i,
];

export function validate(css: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  for (const { pattern, message } of BANNED_PATTERNS) {
    if (pattern.test(css)) errors.push(message);
  }
  for (const r of RISKY_DISPLAY_NONE) {
    if (r.test(css)) {
      errors.push(
        "display: none on a global selector (body, html, #root, button) would lock you out of the UI.",
      );
    }
  }

  // !important on layout properties is a common foot-gun — warn but don't block
  if (/!important/.test(css)) {
    warnings.push(
      "!important is hard to override later. Prefer specificity over !important.",
    );
  }
  if (css.length > 50_000) {
    errors.push("CSS is over 50KB. Most agencies need <2KB; please trim.");
  }

  return { ok: errors.length === 0, errors, warnings };
}

/* --------------------------------------------------- storage */

export async function loadCustomCss(): Promise<CustomCSSRecord | undefined> {
  return idbGet<CustomCSSRecord>(STORAGE_KEY);
}

export async function saveCustomCss(css: string, enabled: boolean): Promise<CustomCSSRecord> {
  const record: CustomCSSRecord = {
    css,
    enabled,
    updatedAt: new Date().toISOString(),
  };
  await idbPut(STORAGE_KEY, record);
  return record;
}

export async function deleteCustomCss(): Promise<void> {
  await idbDelete(STORAGE_KEY);
}

/* --------------------------------------------------- injection */

function disableQueryParam(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("nocss") === "1";
  } catch {
    return false;
  }
}

/**
 * Apply or remove the <style id="agencylens-custom-css"> tag based on the
 * stored record. Idempotent. Call once on app bootstrap + whenever the
 * record changes.
 */
export async function applyCustomCss(record?: CustomCSSRecord): Promise<void> {
  if (typeof document === "undefined") return;

  const r = record ?? (await loadCustomCss());
  const existing = document.getElementById(STYLE_ELEMENT_ID);

  if (disableQueryParam() || !r || !r.enabled || !r.css.trim()) {
    if (existing) existing.remove();
    return;
  }
  const { ok } = validate(r.css);
  if (!ok) {
    // If validation fails we don't apply — saved record stays but is dormant
    if (existing) existing.remove();
    return;
  }

  const tag = existing ?? document.createElement("style");
  tag.id = STYLE_ELEMENT_ID;
  tag.textContent = r.css;
  if (!existing) document.head.appendChild(tag);
}

export function isCustomCssDisabledByQuery(): boolean {
  return disableQueryParam();
}
